import React, { useId } from 'react';

/**
 * Headless tooltip shell: a focusable/hoverable trigger plus a bubble revealed
 * via the `group` + `group-hover`/`group-focus-within` opacity pattern, with the
 * ARIA wiring (`role="tooltip"` + `aria-describedby`) handled once.
 *
 * Styling is supplied by the caller so themed (public-booking) and admin
 * variants can share the structure without sharing a palette:
 *   InfoTooltip    — admin ink palette + info-circle icon
 *   TravelFeeTooltip — public-booking themed tokens + "?" glyph + dark variant
 *
 * @param {React.ReactNode} trigger - Contents of the trigger button (icon/glyph).
 * @param {React.ReactNode} content - Help text shown in the bubble.
 * @param {string} ariaLabel - Accessible name for the trigger button.
 * @param {string} [buttonClassName] - Classes for the trigger button.
 * @param {string} [bubbleClassName] - Classes for the bubble element.
 * @param {string} [arrowClassName] - Classes for the arrow element.
 * @param {string} [className] - Extra classes on the wrapper for placement.
 */
const TooltipBubble = ({
  trigger,
  content,
  ariaLabel,
  buttonClassName = '',
  bubbleClassName = '',
  arrowClassName = '',
  className = '',
}) => {
  const tooltipId = useId();

  return (
    <span className={`relative group inline-flex items-center ${className}`}>
      <button
        type="button"
        className={buttonClassName}
        aria-label={ariaLabel}
        aria-describedby={tooltipId}
      >
        {trigger}
      </button>
      {/* Bubble — shown on hover or when the button has focus */}
      <span id={tooltipId} role="tooltip" className={bubbleClassName}>
        {content}
        {/* Arrow */}
        <span className={arrowClassName} />
      </span>
    </span>
  );
};

export default TooltipBubble;
